import { useMutation } from "@tanstack/react-query"
import { ErrorMessage, Form, Formik } from "formik"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"

import Button from "@/components/Button/Button"
import Error from "@/components/Error/Error"
import TextInput from "@/components/Inputs/TextInput"
import useAxios from "@/hooks/useAxios"
import useFormErr from "@/hooks/useFormErr"
import loginSchema from "@/schemas/loginSchema"
import { useStore } from "@/zustand/store"

import styles from "./Login.module.css"

type LoginData = {
  email: string
  password: string
}

type LoginErrors = {
  general?: string
  email?: string
  password?: string
}

export default function Login() {
  const setAuthData = useStore((state) => state.setAuthData)
  const setAdModalShown = useStore((state) => state.setAdModalShown)
  const { formErr, handleMutationErr } = useFormErr<LoginErrors>()
  const { t } = useTranslation()
  const axios = useAxios()

  const handleLogin = useMutation({
    mutationFn: loginApi,
    onSuccess: (data) => {
      setAuthData(data.data.data)
      // Reset modal state so it shows on login
      setAdModalShown(false)
      toast.success(t("auth.login.messages.success"))
    },
    onError: handleMutationErr,
  })

  function loginApi(data: LoginData) {
    return axios.post(`/student/login`, data)
  }

  return (
    <div>
      <div className={styles.title}>
        <h3 className="fw-bolder mb-2">{t("auth.login.title")}</h3>
        <p className="mb-0">{t("auth.login.subtitle")}</p>
      </div>

      <Formik
        initialValues={{
          email: "",
          password: "",
        }}
        onSubmit={(values) => handleLogin.mutate(values)}
        enableReinitialize
        validationSchema={loginSchema}
      >
        <Form className="pt-3">
          <Error className="my-2">
            {formErr?.general ? formErr?.general : ""}
          </Error>
          <TextInput
            label={t("auth.login.data.email.label")}
            placeholder={t("auth.login.data.email.placeholder")}
            type="email"
            id="login-email-inp"
            name="email"
            className="mb-3"
            error={<ErrorMessage name="email" />}
            backError={formErr?.email}
            as="field"
          />
          <TextInput
            label={t("auth.login.data.password.label")}
            placeholder={t("auth.login.data.password.placeholder")}
            type="password"
            id="login-password-inp"
            name="password"
            className="mb-3"
            error={<ErrorMessage name="password" />}
            backError={formErr?.password}
            as="field"
          />

          <div className="text-center">
            <Button
              className={styles.btn}
              type="submit"
              isLoading={handleLogin.isLoading}
            >
              {t("buttons.login")}
            </Button>
          </div>
        </Form>
      </Formik>
    </div>
  )
}
